import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Map, FileText, Circle, Upload, Download, Share2, Archive, Route, MapPin, Book, FileUp, Grid as Bridge, Info, Map as MapIcon } from 'lucide-react';
import { Toast } from '../../../../components/common/Toast';

export const QuickActions = ({
  activeSurvey,
  isRecording,
  onRecordingStats,
  onLocationInfo,
  onDatabaseStatus,
  onNewSurvey,
  onViewMap,
  onHighPoleSurvey,
  onQuickSurvey,
  onFileUpload,
  onBookSurvey,
  onImport,
  onExport,
  onSharedMap,
  onArchives,
  onRouteTimeline,
  onPOIEditor
}) => {
  const handleDisabledClick = () => {
    setToast({
      type: 'warning',
      message: 'Please load a survey first to use this feature'
    });
  };

  const navigate = useNavigate();
  const [toast, setToast] = useState(null);

  const showRequirementToast = (message) => {
    setToast({
      type: 'warning',
      message: message
    });
  };

  const handleFeatureClick = (feature, handler) => {
    if (!activeSurvey) {
      showRequirementToast('Please load a survey first to use this feature');
      return;
    }
    if (!isRecording && feature !== 'map') {
      showRequirementToast('Please start recording before using this feature');
      return;
    }
    handler();
  };

  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 mt-6 p-4">
      {/* New Survey Button */}
      <button
        onClick={onNewSurvey}
        className="flex items-center gap-3 p-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
      >
        <div className="p-2 bg-blue-500/20 rounded-lg">
          <Plus className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">New Survey</span>
          <span className="text-sm text-white/80">Create a new route survey</span>
        </div>
      </button>

      {/* View Map Button */}
      <button
        onClick={() => handleFeatureClick('map', onViewMap)}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-purple-600 hover:bg-purple-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <Map className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">View Map</span>
          <span className="text-sm text-white/80">Interactive map view</span>
        </div>
      </button>

      {/* Quick Survey Button */}
      <button
        onClick={() => handleFeatureClick('quick', onQuickSurvey)}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey || !isRecording
            ? 'bg-gray-600 cursor-not-allowed opacity-50'
            : 'bg-green-600 hover:bg-green-700 text-white'
        }`}
        disabled={!activeSurvey || !isRecording}
      >
        <div className="p-2 bg-green-500/20 rounded-lg">
          <MapPin className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Quick Survey</span>
          <span className="text-sm text-white/80">
            {!activeSurvey ? 'Select a survey first' : 'Create quick measurements'}
          </span>
        </div>
      </button>

      {/* Survey Information Button */}
      <button
        onClick={() => navigate('/roadscope/survey-info')}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-indigo-500/20 rounded-lg">
          <Info className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Survey Information</span>
          <span className="text-sm text-white/80">View and manage survey details</span>
        </div>
      </button>

      {/* Other Quick Actions */}
      <button
        onClick={() => handleFeatureClick('highpole', onHighPoleSurvey)}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey || !isRecording
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-yellow-600 hover:bg-yellow-700 text-white'
        }`}
        disabled={!activeSurvey || !isRecording}
      >
        <div className="p-2 bg-yellow-500/20 rounded-lg">
          <Route className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">High Pole</span>
          <span className="text-sm text-white/80">Record high pole measurements</span>
        </div>
      </button>

      <button
        onClick={() => handleFeatureClick('upload', onFileUpload)}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey || !isRecording
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
        }`}
        disabled={!activeSurvey || !isRecording}
      >
        <div className="p-2 bg-indigo-500/20 rounded-lg">
          <FileUp className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Upload Files</span>
          <span className="text-sm text-white/80">
            {!activeSurvey ? 'Select a survey first' : !isRecording ? 'Start recording first' : 'Add documents and photos'}
          </span>
        </div>
      </button>

      <button
        onClick={onBookSurvey}
        className="flex items-center gap-3 p-4 bg-pink-600 hover:bg-pink-700 text-white rounded-lg transition-colors"
      >
        <div className="p-2 bg-pink-500/20 rounded-lg">
          <Book className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Book Survey</span>
          <span className="text-sm text-white/80">Schedule a new survey</span>
        </div>
      </button>

      <button
        onClick={onImport}
        className="flex items-center gap-3 p-4 bg-orange-600 hover:bg-orange-700 text-white rounded-lg transition-colors"
      >
        <div className="p-2 bg-orange-500/20 rounded-lg">
          <Upload className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Import</span>
          <span className="text-sm text-white/80">Import external data</span>
        </div>
      </button>

      <button
        onClick={activeSurvey ? onExport : handleDisabledClick}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-cyan-600 hover:bg-cyan-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-cyan-500/20 rounded-lg">
          <Download className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Export</span>
          <span className="text-sm text-white/80">Export survey data</span>
        </div>
      </button>

      <button
        onClick={activeSurvey ? onSharedMap : handleDisabledClick}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-emerald-600 hover:bg-emerald-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-emerald-500/20 rounded-lg">
          <Share2 className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Shared Map</span>
          <span className="text-sm text-white/80">View shared surveys</span>
        </div>
      </button>

      {/* Structures on Route */}
      <button
        onClick={() => navigate('structures')}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-indigo-600 hover:bg-indigo-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-indigo-500/20 rounded-lg">
          <Bridge className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Structures on Route</span>
          <span className="text-sm text-white/80">Find bridges and tunnels</span>
        </div>
      </button>

        <button
          onClick={() => navigate('/roadscope/osow-planner')}
          className="flex items-center gap-3 p-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
        >
          <div className="p-2 bg-purple-500/20 rounded-lg">
            <Route className="w-6 h-6" />
          </div>
          <div className="text-left">
            <span className="font-medium block">OS/OW Route Planner</span>
            <span className="text-sm text-white/80">Plan routes for oversized loads</span>
          </div>
        </button>

      <button
        onClick={() => navigate('/roadscope/mtmd-map')}
        className="flex items-center gap-3 p-4 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors"
      >
        <div className="p-2 bg-purple-500/20 rounded-lg">
          <MapIcon className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Carte MTMD</span>
          <span className="text-sm text-white/80">Couches WMS du Québec</span>
        </div>
      </button>

      <button
        onClick={onArchives}
        className="flex items-center gap-3 p-4 bg-teal-600 hover:bg-teal-700 text-white rounded-lg transition-colors"
      >
        <div className="p-2 bg-teal-500/20 rounded-lg">
          <Archive className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Archives</span>
          <span className="text-sm text-white/80">View archived surveys</span>
        </div>
      </button>

      <button
        onClick={() => handleFeatureClick('profile', () => navigate('/roadscope/profile'))}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey || !isRecording
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-rose-600 hover:bg-rose-700 text-white'
        }`}
        disabled={!activeSurvey || !isRecording}
      >
        <div className="p-2 bg-rose-500/20 rounded-lg">
          <Route className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Road Profile</span>
          <span className="text-sm text-white/80">Record elevation profiles</span>
        </div>
      </button>

      <button
        onClick={activeSurvey ? onRouteTimeline : handleDisabledClick}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-amber-600 hover:bg-amber-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-amber-500/20 rounded-lg">
          <Route className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">Route Timeline</span>
          <span className="text-sm text-white/80">View route history</span>
        </div>
      </button>

      <button
        onClick={activeSurvey ? onPOIEditor : handleDisabledClick}
        className={`flex items-center gap-3 p-4 rounded-lg transition-colors ${
          !activeSurvey 
            ? 'bg-gray-600 cursor-not-allowed opacity-50' 
            : 'bg-violet-600 hover:bg-violet-700 text-white'
        }`}
        disabled={!activeSurvey}
      >
        <div className="p-2 bg-violet-500/20 rounded-lg">
          <FileText className="w-6 h-6" />
        </div>
        <div className="text-left">
          <span className="font-medium block">POI Editor</span>
          <span className="text-sm text-white/80">Manage points of interest</span>
        </div>
      </button>
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}
    </div>
  );
};